<template>
  <loading-data-page-container :loading="loading"
                               :loading-error="loadingError"
                               :loading-error-message="loadingErrorMsg"
                               max-width
                               class="profile-page">

    <template v-if="!loading && !loadingError">

      <!-- read mode: view of all sections -->
      <template v-if="!edit">

        <success-failure-display v-if="updated"
                                 message="Wir haben Ihre Benutzerdaten erfolgreich für Sie gespeichert." />

        <details-section :items="accountItems"
                         title="Benutzerkonto"
                         divider
                         editable
                         @edit="setEdit('passwort')" />

        <details-section :items="personItems"
                         title="Person"
                         divider
                         editable
                         @edit="setEdit('person')" />

        <details-section :items="firmaItems"
                         title="Unternehmen"
                         divider
                         editable
                         @edit="setEdit('firma')" />

        <details-section :items="kontaktItems"
                         title="Kontakt"
                         divider
                         editable
                         @edit="setEdit('kontakt')" />

        <details-section :items="adresseItems"
                         title="Adresse"
                         divider
                         editable
                         @edit="setEdit('adresse')" />

        <details-section :items="bankItems"
                         :divider="user.ktoinhaberanderer !== '1'"
                         title="Bankverbindung"
                         editable
                         @edit="setEdit('bank')" />

        <details-section v-if="user.ktoinhaberanderer === '1'"
                         :items="kontoinhaberItems"
                         divider
                         title="Kontoinhaber" />

        <details-section id="infoDetails"
                         :items="infoItems"
                         title="Versandoptionen"
                         divider
                         editable
                         @edit="setEdit('info')" />

        <div class="semanticsection-title2 primary--text">Sonstiges</div>

        <div id="sonstiges-container">
          <div id="verified">
            <v-icon :color="user.verifiziert==='0' ? 'error' : 'success'"
                    class="mr-2">
              {{ user.verifiziert==='0' ? 'clear' : 'check' }}
            </v-icon>
            {{ `Sie sind ${user.verifiziert==='0' ? 'nicht ': ''}als Makler verifiziert.` }}
          </div>

          <span class="datenschutz">Hinweise zur Verarbeitung Ihrer personenbezogenen Daten entnehmen Sie bitte unserer
            <a :href="datenschutzUrl"
               target="_blank">Datenschutzerklärung
            </a>.
          </span>
        </div>

      </template>

      <!-- edit mode:  edit view of one section -->
      <template v-else-if="edit==='person'">
        <edit-section :keys="['anrede', 'titel', 'vorname', 'nachname', 'staat', 'geburt']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Person"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-person :user-form="slotProps.userForm" />
          </template>
        </edit-section>
      </template>

      <template v-else-if="edit==='firma'">
        <edit-section :keys="['firma', 'rechtsform', 'maklerverbund', 'gruendungsjahr', 'dihk']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Unternehmen"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-firma :user-form="slotProps.userForm" />
          </template>
        </edit-section>
      </template>

      <template v-else-if="edit==='kontakt'">
        <edit-section :keys="['email', 'tel', 'fax', 'mobile']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Kontakt"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-kontakt :user-form="slotProps.userForm" />
          </template>
        </edit-section>
      </template>

      <template v-else-if="edit==='adresse'">
        <edit-section :keys="['strasse', 'hausnummer', 'adresszusatz', 'plz', 'ort', 'postfach', 'pplz']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Adresse"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-adresse :user-form="slotProps.userForm" />
          </template>
        </edit-section>
      </template>

      <template v-else-if="edit==='bank'">
        <edit-section :keys="['ktoiban', 'ktobic', 'ktoinhaberanderer', 'ktoinhaberanrede', 'ktoinhabervorname',
                        'ktoinhabernachname']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Bankverbindung"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-bank :user-form="slotProps.userForm" />
          </template>
        </edit-section>
      </template>

      <template v-else-if="edit==='info'">
        <edit-section :keys="['originalwas', 'kopiewas', 'kopiepost', 'kopieadresse', 'provmail', 'provmailadresse']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Versandoptionen"
                      @cancel="setEdit(null)"
                      @update-profile="updateProfileData"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-info :user-form="slotProps.userForm"
                       :default-mail="defaultMail" />
          </template>
        </edit-section>
      </template>

      <!-- change password mode -->
      <template v-else-if="edit==='passwort'">
        <edit-section :keys="['newpassword', 'oldpassword']"
                      :user="user"
                      :disable-buttons="disableButtons"
                      title="Passwort ändern"
                      @cancel="setEdit(null)"
                      @update-profile="updatePassword"
                      @error="showSnackbar('Bitte überprüfen Sie Ihre Eingaben.')">
          <template slot-scope="slotProps">
            <edit-passwort :user-form="slotProps.userForm" />
          </template>
          <site-tooltip slot="title-addition">Ihr Passwort muss mindestens 8 Zeichen lang sein und muss mindestens einen Buchstaben und eine Ziffer enthalten.
          </site-tooltip>
        </edit-section>
      </template>

    </template>

    <!-- error message for editing data -->
    <v-snackbar v-model="snackbar"
                :timeout="4000"
                color="error"
                bottom
                multi-line>
        <span class="snackbar-message text-center">
          <v-icon color="white">warning</v-icon>{{ message }}</span>
    </v-snackbar>

  </loading-data-page-container>
</template>
<script>
import EditSection from "@/components/userProfile/EditSection";
import EditPerson from "@/components/userProfile/EditPerson";
import EditFirma from "@/components/userProfile/EditFirma";
import EditKontakt from "@/components/userProfile/EditKontakt";
import EditAdresse from "@/components/userProfile/EditAdresse";
import EditBank from "@/components/userProfile/EditBank";
import EditInfo from "@/components/userProfile/EditInfo";
import EditPasswort from "@/components/userProfile/EditPasswort";
import SuccessFailureDisplay from "@/components/SuccessFailureDisplay";
import DetailsSection from "../../components/userProfile/DetailsSection";
import dummyUsers from "@/components/userProfile/dummyData";
import httpService from '../../helper/httpService';
import {SCHRIFTWECHSELART_VALUES, VSSN_VALUE, SS_VALUE, RECHTSFORM_VALUES, MAKLERVERBUND_VALUES} from "@/components/userProfile/valueLists";
import {datenschutzUrl} from "@/store/config/externalLinks";
import LoadingDataPageContainer from "@/components/kundenverwaltung/LoadingDataPageContainer";
import {CONNECTION_ERROR_MESSAGE} from "@/helper/httpService";

export default {
  name: "BenutzerProfilPage",
  components: {
    LoadingDataPageContainer,
    DetailsSection,
    EditSection,
    EditPerson,
    EditFirma,
    EditKontakt,
    EditAdresse,
    EditBank,
    EditInfo,
    EditPasswort,
    SuccessFailureDisplay
  },
  data() {
    return {
      loading: true,
      loadingError: false,
      loadingErrorMsg: '',
      disableButtons: false,
      snackbar: false,
      updated: false,
      message: '',
      edit: null,

      defaultMail: '',
      user: {
        anrede: "kA",
        titel: "",
        vorname: "",
        nachname: "",
        staat: "Deutschland",
        geburt: "",
        firma: "",
        rechtsform: null,
        maklerverbund: null,
        gruendungsjahr: "",
        dihk: "",
        loginname: "",
        email: "",
        mobile: "",
        tel: "",
        fax: "",
        strasse: "",
        hausnummer: "",
        adresszusatz: "",
        plz: "",
        ort: "",
        postfach: "",
        pplz: "",
        ktoiban: '',
        ktobic: '',
        ktoinhaberanderer: "0",
        ktoinhaberanrede: "kA",
        ktoinhabertitel: "",
        ktoinhabervorname: "",
        ktoinhabernachname: "",
        ktoinhaberstrasse: "",
        ktoinhaberhausnummer: "",
        ktoinhaberadresszusatz: "",
        ktoinhaberplz: "",
        ktoinhaberort: "",
        //originalwas and kopiewas have to be sums of powers of 2
        originalwas: "0", //which originals to send (always per post), value: 0 to 3
        kopiewas: "0", //which copies to send, value: 0 to 31
        kopiepost: "0", // 1 => per Post, 0 => per Mail
        kopieadresse: "", // kopiepost == 0 => Adresse für Mailversand
        provmail: "0", // 0 => per Post, 1 => per Mail
        provmailadresse: "", // provmail == 1 => Adresse für Mailversand
        verifiziert: "",
        newpassword: "",
        oldpassword: "",
        //obsolete attributes, are only added so they can be cleared when IBAN is changed
        ktobank: "",
        ktoblz: "",
        ktonr: "",
        ktoinhaber: "",
        ktoinhabergeburt: ""
      },
      datenschutzUrl
    };
  },
  computed: {
    personItems: function () {
      return [
        { label: "Anrede", value: this.user.anrede && this.user.anrede !== "kA" ? this.user.anrede : "keine Angabe" },
        { label: "Titel", value: this.user.titel },
        { label: "Name", value: `${this.user.vorname} ${this.user.nachname}` },
        { label: "Geburtsdatum", value: this.user.geburt },
        { label: "Staatsangehörigkeit Land", value: this.user.staat ? this.user.staat : "Deutschland" }];
    },
    firmaItems: function () {
      return (this.user.firma ?
          [{ label: "Name", value: this.user.firma },
            { label: "Rechtsform", value: this.getRechtsform(this.user.rechtsform) },
            { label: "Gründungsjahr", value: this.user.gruendungsjahr }
          ] :
          [{ label: "Name", value: this.user.firma }]
      ).concat([
        { label: "Registriernummer DIHK", value: this.user.dihk },
        { label: "Mitglied in einem Maklerverbund", value: this.getMaklerverbund(this.user.maklerverbund) }]);
    },
    accountItems: function () {
      return [
        { label: "Anmeldename", value: this.user.loginname },
        { label: "Passwort", value: '**********' }];
    },
    kontaktItems: function () {
      return [
        { label: "E-Mail", value: this.user.email },
        { label: "Festnetz- / Mobilnummer", value: this.user.tel },
        { label: "Alternative Festnetz- / Mobilnummer", value: this.user.mobile },
        { label: "Fax", value: this.user.fax }];
    },
    adresseItems: function () {
      return [
        { label: "Postfach", value: this.user.postfach },
        {
          label: "Strasse und Hausnr.",
          value: `${this.user.strasse} ${this.user.hausnummer} ${this.user.adresszusatz}`
        },
        { label: "PLZ und Ort", value: `${this.user.plz} ${this.user.ort}` }
        ];
    },
    bankItems: function () {
      let items = [
        { label: "IBAN", value: this.user.ktoiban }
      ];
      if (this.$helpers.isIbanAusland(this.user.ktoiban)) items.push({ label: "BIC", value: this.user.ktobic });
      return items.concat([{
        label: "Vermittler ist Kontoinhaber?",
        value: this.user.ktoinhaberanderer === '1' ? "Nein" : "Ja"
      }]);
    },
    kontoinhaberItems: function () {
      return [
        { label: "Anrede", value: this.user.ktoinhaberanrede && this.user.ktoinhaberanrede !== "kA" ? this.user.ktoinhaberanrede : "keine Angabe" },
        { label: "Titel", value: this.user.ktoinhabertitel },
        { label: "Name", value: `${this.user.ktoinhabervorname} ${this.user.ktoinhabernachname}` },
        {
          label: "Strasse und Hausnr.",
          value: `${this.user.ktoinhaberstrasse} ${this.user.ktoinhaberhausnummer} ${this.user.ktoinhaberadresszusatz}`
        },
        { label: "PLZ und Ort", value: `${this.user.ktoinhaberplz} ${this.user.ktoinhaberort}` }
      ];
    },
    infoItems: function () {
      const originalText = 'Original per Post';
      const kopieText = `Kopie per ${this.user.kopiepost === '1' ? 'Post' : 'E-Mail'}`;
      const postTooltip = `${this.user.strasse} ${this.user.hausnummer} ${this.user.adresszusatz}, ${this.user.plz} ${this.user.ort}`;
      return SCHRIFTWECHSELART_VALUES.map((art) => {
        let value = this.$helpers.isBitSet(this.user.originalwas, art.value === VSSN_VALUE ? VSSN_VALUE : SS_VALUE) ?
          originalText : this.user.kopiewas === '0' || !this.$helpers.isBitSet(this.user.kopiewas, art.value)  ?
          'kein Versand' :
          kopieText;
        let valueTooltip = this.$helpers.isBitSet(this.user.originalwas, art.value === VSSN_VALUE ? VSSN_VALUE : SS_VALUE) ?
          postTooltip : this.user.kopiewas === '0' || !this.$helpers.isBitSet(this.user.kopiewas, art.value) ?
          null : this.user.kopiepost === "1" ?
          postTooltip :
          this.user.kopieadresse;
        return {
          label: art.text,
          value: value,
          tooltip: art.tooltip,
          valueTooltip: valueTooltip
        };
      }).concat([{
        label: 'Courtage-Abrechnungen',
        value: this.user.provmail === '0' ? 'per Post' : 'per E-Mail',
        valueTooltip: this.user.provmail === '0' ? postTooltip : this.user.provmailadresse
      }]);
    }
  },
  created() {
    if (this.$route.params['editversand']) {
      this.setEdit('info');
    }
    httpService.getProfile()
      .then(content => {
        this.user = Object.assign({}, this.user, content);
        if (!this.user.maklerverbund && content.maklerverbundsonstige) {
          this.user.maklerverbund = content.maklerverbundsonstige;
        }
        if (!this.user.rechtsform) {
          this.user.rechtsform = null;
        }
        if (!this.user.anrede) this.user.anrede = "kA";
        if (!this.user.ktoinhaberanrede) this.user.ktoinhaberanrede = "kA";
        this.defaultMail = this.user.email;
        this.loading = false;
      })
      .catch(err => {
        console.log(`GET PROFILE faild with ${err.status}: ${err.message}`);
        //todo delete this
        if (this.$store.state.env.environment === 'development') {
          this.user = Object.assign({}, this.user, dummyUsers[0]);
          if (!this.user.maklerverbund && dummyUsers[0].maklerverbundsonstige) {
            this.user.maklerverbund = dummyUsers[0].maklerverbundsonstige;
          }
          if (!this.user.rechtsform) {
            this.user.rechtsform = null;
          }
          this.defaultMail = this.user.email;
          this.loading = false;
        } else {
          this.loadingErrorMsg = err.status < 500 ? 'Benutzer nicht gefunden' : CONNECTION_ERROR_MESSAGE;
          this.loadingError = true;
          this.loading = false;
        }
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    getRechtsform(value) {
      let rf = RECHTSFORM_VALUES.filter(m => m.value === value)[0];
      return rf ? rf.text : value;
    },
    getMaklerverbund(value) {
      let mv = MAKLERVERBUND_VALUES.filter(m => m.value === value)[0];
      return mv ? mv.text : value;

    },
    setEdit: function (section, updated) {
      this.edit = section;
      this.updated = !!updated;
      window.scrollTo(0, 0);
    },
    updateProfileData: function (data) {
      this.disableButtons = true;
      if (data.ktoiban && this.user.ktoiban !== data.ktoiban) this.clearObsoleteData();
      let updatedUser = Object.assign({}, this.user, data);
      updatedUser.changelog = this.getChangelog(data);
      delete updatedUser.oldpassword;
      delete updatedUser.newpassword;
      httpService.updateProfile(updatedUser)
        .then(data => {
          this.user = Object.assign({}, this.user, data);
          if (!this.user.anrede) this.user.anrede = "kA";
          if (!this.user.ktoinhaberanrede) this.user.ktoinhaberanrede = "kA";
          this.defaultMail = this.user.email;
          this.setEdit(null, true);
        })
        .catch((err) => {
          console.log(`UPDATE PROFILE failed with ${err.status}: ${err.message}`);
          this.showSnackbar('Speichern fehlgeschlagen.');
        })
        .finally(() => this.disableButtons = false );
    },
    updatePassword: function (data) {
      this.disableButtons = true;
      httpService.changePassword(data)
        .then((res) => {
          if (res) {
            this.setEdit(null, true);
          } else {
            this.showSnackbar('Falsches Passwort.');
          }
        })
        .catch(() => {
          this.showSnackbar('Änderung des Passworts fehlgeschlagen.');
        })
        .finally(() => this.disableButtons = false );
    },
    showSnackbar: function (msg) {
      this.message = msg;
      this.snackbar = true;
    },
    clearObsoleteData() {
      if (this.user.ktobank) this.user.ktobank = "";
      if (this.user.ktoblz) this.user.ktoblz = "";
      if (this.user.ktonr) this.user.ktonr = "";
      if (this.user.ktoinhaber) this.user.ktoinhaber = "";
      if (this.user.ktoinhabergeburt) this.user.ktoinhabergeburt = "";
    },
    getChangelog(data) {
      const changelog = {};
      Object.keys(data).forEach(key => {
        const oldvalue = this.user[key] || "";
        const newvalue = data[key] || "";
        if (oldvalue.toString() !== newvalue.toString()) changelog[key] = {
          oldvalue,
          newvalue
        };
      });
      return changelog;
    }
  }
};
</script>
<style>

.profile-page #infoDetails .details-table tr > td:last-child {
  white-space: nowrap !important;
}

.profile-page #verified {
  display: flex;
  align-items: center;
}

.profile-page #verified .v-icon {
  border: solid 1px !important;
  border-radius: 3px !important;
}

.profile-page #sonstiges-container {
  width: 448px;
  margin: 0 auto;
  padding: 0 24px;
}

.profile-page .datenschutz {
  display: block;
  margin-top: 40px;
}
</style>
